/**
 * grnMix — what a delivery is made of: sets, top-only pieces, pants-only
 * pieces.
 *
 * Mikko, 2026-09-30: a SKU ending -TO or -PO before its size
 * (SS028-MDBLU-TO-XXS, SS032-BLK-PO-S) is a TOP ONLY or PANTS ONLY garment.
 * It is ONE PIECE, not a set, and counting it as a set overstates what was
 * delivered — and, downstream, what a subcontractor is owed. So a GRN says
 * "28 SETS | 2 Top Only | 2 Pants Only" and never folds the pieces into the
 * set count.
 *
 * Pure and I/O-free. The marker is read off the SKU's second-to-last token
 * only, the same place garmentName.splitPlanSuffix reads it, so a colour that
 * merely contains the letters TO or PO can never be mistaken for a plan.
 */

export type GrnPlan = "SET" | "TOP" | "PANTS";

const planCache = new Map<string | null | undefined, GrnPlan>();

/** SET, TOP (top only) or PANTS (pants only) for one SKU code. */
export function planOfSku(sku: string | null | undefined): GrnPlan {
  const hit = planCache.get(sku as string);
  if (hit) return hit;
  const tokens = String(sku ?? "").trim().toUpperCase().split("-").filter(Boolean);
  let plan: GrnPlan = "SET";
  if (tokens.length >= 3) {
    const marker = tokens[tokens.length - 2];
    if (marker === "TO") plan = "TOP";
    else if (marker === "PO") plan = "PANTS";
  }
  // The same few thousand SKUs come round for every WIP, every recompute
  // (0.17 s of a GRN lookup at 36,000 receipts). Bounded, and cleared rather
  // than evicted: a catalogue is small, a runaway is not.
  if (planCache.size >= 50_000) planCache.clear();
  planCache.set(sku as string, plan);
  return plan;
}

export interface IGrnMix {
  sets: number;
  topOnly: number;
  pantsOnly: number;
}

export function grnMix(lines: readonly { qty: number; sku?: string | null }[]): IGrnMix {
  const mix: IGrnMix = { sets: 0, topOnly: 0, pantsOnly: 0 };
  for (const l of lines) {
    const q = l.qty || 0;
    const plan = planOfSku(l.sku);
    if (plan === "TOP") mix.topOnly += q;
    else if (plan === "PANTS") mix.pantsOnly += q;
    else mix.sets += q;
  }
  return mix;
}

/** "28 sets | 2 Top Only | 2 Pants Only" — a part that is zero is left out,
 *  and a delivery with nothing at all still reads "0 sets". */
export function mixText(mix: IGrnMix, opts: { upperSets?: boolean } = {}): string {
  const setsWord = opts.upperSets ? "SETS" : (mix.sets === 1 ? "set" : "sets");
  const parts: string[] = [];
  if (mix.sets > 0 || (mix.topOnly === 0 && mix.pantsOnly === 0)) parts.push(`${mix.sets} ${setsWord}`);
  if (mix.topOnly > 0) parts.push(`${mix.topOnly} Top Only`);
  if (mix.pantsOnly > 0) parts.push(`${mix.pantsOnly} Pants Only`);
  return parts.join(" | ");
}

/** The pieces only — "2 Top Only | 2 Pants Only", or "" when there are none. */
export function piecesText(mix: IGrnMix): string {
  const parts: string[] = [];
  if (mix.topOnly > 0) parts.push(`${mix.topOnly} Top Only`);
  if (mix.pantsOnly > 0) parts.push(`${mix.pantsOnly} Pants Only`);
  return parts.join(" | ");
}
